import { i18n } from '@/main';
import { extend } from 'vee-validate';
import { required, max, min, email } from 'vee-validate/dist/rules';

/**
 * Vee-validate utilise son propre système de localisation : https://vee-validate.logaretm.com/v3/guide/localization.html#using-the-default-i18n
 * Les imports suivants permettent d'utiliser les "locales" fournies. Seuls les messages prédéfinis par vee-validate sont inclus :
 * import en from 'vee-validate/dist/locale/en.json';
 * import fr from 'vee-validate/dist/locale/fr.json';
 * 
 * localize({
 *      en,
 *      fr
 * });
 * 
 * Afin de conserver notre propre système de localisation, j'ai redéfini les messages des validateurs vee-validate en utilisant notre instance i18n. 
 * Les messages sont définis dans translate.json.
 * Je n'ai pas trouvé comment utiliser des fichiers locaux avec cette méthode.
 * Comme les validateurs peuvent être utilisés de façon globale dans l'application, cela ne pose pas de problème. 
 */

/** Validateur required */
extend('required', {
  ...required,
  message: (_, { length }) => i18n.t('Required', { length:length}).toString()
});

/** Validateur pour tester le nombre maximum de caractère d'une string */
extend('max', {
    ...max,
    message: (_, { length }) => i18n.t('MaxLimitReach', { length:length}).toString()
});

/** Validateur pour tester le nombre minimun de caractère d'une string */
extend('min', {
    ...min,
    message: (_, { length }) => i18n.t('MinLimitReach', { length:length}).toString()
});

/** Validateur pour tester la syntaxe d'une adresse email */
extend('email', {
    ...email,
    message: (_, { length }) => i18n.t('EmailInvalid', { length:length}).toString()
});

/** Validateur pour tester les chaines de caractères sans caractères spéciaux*/
extend('NoSpecialChar', {
    validate(value) {
        if (value === null || value === undefined || value === '') {
            return true;
        }

        const stringValue = String(value);
        // On retire les espaces et tirets pour la validation si c'est votre logique
        const cleanValue = stringValue.replace(/ /g, '').replace(/-/g, '');
        
        // \p{L} correspond à n'importe quelle lettre dans n'importe quelle langue
        // \p{N} correspond à n'importe quel chiffre
        // Le flag 'u' (unicode) est OBLIGATOIRE
        return /^[\p{L}\p{N}'-]+$/u.test(cleanValue);
    },
    message: (_, { length }) => i18n.t('NoSpecialChar', { length: length }).toString()
});

/** Validateur pour tester la syntaxe d'un numéro de téléphone français classique 0... +33... 0033... + numéros au format internationaux E.164*/
extend('phoneNumber', {
    /**
     * /^(?:\+33|0033|0)[1-9]\d{8}$/ -> Tolérence numéro français
     * 
     * ^ -> début de chaîne
     * (?:\+33|0033|0) -> accepte les préfixes +33, 0033 ou 0
     * [1-9] -> premier chiffre de l’abonné (jamais 0)
     * \d{8} -> exactement 8 chiffres après
     * $ fin de chaîne
     * 
     * ^(?:\+|00)[1-9]\d{6,14}$  -> validation des numéros internationaux au format E.164 (standard mondial)
     */
    validate(value : string) {
        const cleanValue = value.replaceAll(' ','').replaceAll('-','');
        return /^(?:\+33|0033|0)[1-9]\d{8}$/.test(cleanValue) || /^(?:\+|00)[1-9]\d{6,14}$/.test(cleanValue) 
    },
    message: (_, { length }) => i18n.t('PhoneInvalid', { length:length}).toString()
});

extend('noSpace',{
    /**
     * Vérifie que la saisie ne comporte pas d'espace
     */
    validate(value : string) {
        return !value.includes(' '); 
    },
    message: (_, { length }) => i18n.t('NoSpaceInvalid', { length:length}).toString()
})

/**
 * Validateur de PCE GRDF
 */
extend('pceGRDF', {
    /**
     * Doit commencer par GI + 6 chiffres
     * OU
     * Doit contenir 14 chiffres*
     * 
     * /^(GI\d{6}|\d{14})$/;
     */
    validate(value: string){
        
        if (!value) {
            return false;
        }

        const trimValue = value.replaceAll(' ', '');
        return /^(GI\d{6}|\d{14})$/.test(trimValue)
    },
    message: () => i18n.t('GRDFPceInvalid').toString()
})

/**
 * Validateur téléphone demande d'accès GRDF
 */
extend('GRDFPhone', {
    /**
     * ^0[6-7]{1}\d{8}$ --> Format FR pour les portables
     */
    validate(value: string) {
        if (!value) {
            return false;
        }

        const trimValue = value.replaceAll(' ', '');
        return /^0[6-7]{1}\d{8}$/.test(trimValue)
    },
    message: () => i18n.t('GRDFPhoneInvalid').toString()
})

/**
 * Validateur de code postal FR
 */
extend('Zipcode', {
    /**
     * ^[0-9]{5,5}$ Regex pour les codes postaux français
     */
    validate(value: string){
        if (!value) {
            return false;
        }
        
        const trimValue = value.replaceAll(' ', '');
        return /^[0-9]{5,5}$/.test(trimValue)
    },
    message: () => i18n.t('ZipcodeInvalid').toString()
})


/**
 * Validateur de Siret d'entreprise FR
 */
extend('Siret', {
    /**
     * /^\d{14}$/ Regex pour les siret d'entreprises française
     */
    validate(value: string){
        if (!value && value.trim() == '') {
            return false;
        }

        const trimValue = value.replaceAll(' ', '');
        return /^\d{14}$/.test(trimValue)
    },
    message: () => i18n.t('SiretInvalid').toString()
})

extend('min_years', {
  validate(value: any, params: { min?: string | Date | number }) {
    if (!value) return true; // Laisser 'required' gérer le vide

    const { min } = params;
    if (min == null) return true;

    const yearsToAdd = parseInt(String(min), 10);
    const selectedDate = new Date(value);
    
    // 1. On prend la date d'aujourd'hui comme référence
    const limitDate = new Date();
    
    // 2. On ajoute le nombre d'années requis
    limitDate.setFullYear(limitDate.getFullYear() + yearsToAdd);

    // 3. On remet les heures à 0 pour comparer uniquement les jours
    selectedDate.setHours(0, 0, 0, 0);
    limitDate.setHours(0, 0, 0, 0);

    // 4. La date sélectionnée doit être >= à Aujourd'hui + X années
    return selectedDate >= limitDate;
  },
  params: ['min'],
  // Utilisation de la syntaxe de placeholder VeeValidate pour le message
  message: 'La date doit être d\'au moins {min} année(s) à partir de maintenant.'
});

extend('max_years', {
  validate(value: any, params: { max?: string | Date | number }) {
    if (!value) return true; // Laisser 'required' gérer le vide

    const { max } = params;
    if (max == null) return true;

    const yearsToAdd = parseInt(String(max), 10);
    const selectedDate = new Date(value);
    
    // 1. On prend la date d'aujourd'hui comme référence
    const limitDate = new Date();
    
    // 2. On ajoute le nombre d'années requis
    limitDate.setFullYear(limitDate.getFullYear() + yearsToAdd);

    // 3. On remet les heures à 0 pour comparer uniquement les jours
    selectedDate.setHours(0, 0, 0, 0);
    limitDate.setHours(0, 0, 0, 0);

    // 4. La date sélectionnée doit être >= à Aujourd'hui + X années
    return selectedDate <= limitDate;
  },
  params: ['max'],
  // Utilisation de la syntaxe de placeholder VeeValidate pour le message
  message: 'La date doit être d\'au plus {max} année(s) à partir de maintenant.'
});

/**
 * Validateur de date strictement antérieure à la date du jour (ex : date de naissance)
 */
extend('before_today', {
  validate(value: any) {
    if (!value) return true; // Laisser 'required' gérer le vide

    const selectedDate = new Date(value);

    // Saisie manuelle illisible : le format est aussi signalé par le DatePicker
    if (isNaN(selectedDate.getTime())) return false;

    const today = new Date();

    // Comparaison au jour près : la date doit être strictement antérieure à aujourd'hui
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return selectedDate < today;
  },
  message: 'La date doit être antérieure à la date du jour.'
});
